import React from 'react';
import { useLang } from '../../contexts/LangContext';
import { APP_NAME } from '../../utils/appConfig';
import { LogoSettings } from '../../services/settingsApi';
import { MediaImage } from './MediaImage';

interface AppBrandMarkProps {
  logo?: LogoSettings | null;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'light' | 'dark';
  showSubtitle?: boolean;
  layout?: 'inline' | 'stacked';
  className?: string;
}

const sizeClasses = {
  sm: {
    box: 'h-10 max-w-[7.5rem] rounded-lg px-1',
    placeholderBox: 'w-10 h-10 rounded-lg',
    image: 'h-10 w-auto max-w-full object-contain object-left',
    lightImage: 'h-10 w-10 object-contain',
    icon: 'w-5 h-5',
    title: 'text-xl',
    subtitle: 'text-xs',
    letter: 'text-lg',
    lightRadius: 'rounded-xl',
  },
  md: {
    box: 'h-12 max-w-[9rem] rounded-xl px-1',
    placeholderBox: 'w-12 h-12 rounded-xl',
    image: 'h-12 w-auto max-w-full object-contain object-left',
    lightImage: 'h-12 w-12 object-contain',
    icon: 'w-6 h-6',
    title: 'text-2xl',
    subtitle: 'text-sm',
    letter: 'text-xl',
    lightRadius: 'rounded-2xl',
  },
  lg: {
    box: 'h-24 max-w-[18rem] rounded-3xl px-2',
    placeholderBox: 'w-24 h-24 rounded-3xl',
    image: 'h-24 w-auto max-w-full object-contain',
    lightImage: 'h-28 w-28 object-contain',
    icon: 'w-12 h-12',
    title: 'text-4xl',
    subtitle: 'text-xl',
    letter: 'text-3xl',
    lightRadius: 'rounded-3xl',
  },
};

export const AppBrandMark: React.FC<AppBrandMarkProps> = ({
  logo,
  size = 'md',
  variant = 'dark',
  showSubtitle = true,
  layout = 'inline',
  className = '',
}) => {
  const { t } = useLang();
  const styles = sizeClasses[size];
  const logoSrc = logo?.logoUrl || logo?.logoPath || '';
  const hasLogo = Boolean(logoSrc);
  const isLight = variant === 'light';
  const isStacked = layout === 'stacked';

  const lightLogoShell = isLight && hasLogo;

  const logoBox = (
    <div
      className={`${
        hasLogo
          ? lightLogoShell
            ? `inline-flex overflow-hidden ${styles.lightRadius} shadow-2xl ring-1 ring-white/30`
            : styles.box
          : styles.placeholderBox
      } flex items-center justify-center flex-shrink-0 ${
        hasLogo
          ? lightLogoShell
            ? ''
            : 'bg-white border border-gray-200'
          : isLight
            ? 'bg-white bg-opacity-20 backdrop-blur-sm'
            : 'bg-gradient-to-r from-cyan-500 to-blue-500'
      } ${isStacked ? 'mx-auto mb-8' : ''}`}
    >
      {hasLogo ? (
        <MediaImage
          src={logoSrc}
          alt={APP_NAME}
          className={lightLogoShell ? styles.lightImage : styles.image}
          fallbackClassName={lightLogoShell ? styles.lightImage : `${styles.image} max-h-full`}
          fallbackText=""
        />
      ) : isLight ? (
        <svg className={`${styles.icon} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h2M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ) : (
        <span className={`text-white font-bold ${styles.letter}`}>{APP_NAME.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );

  const textBlock = (
    <div className={isStacked ? 'text-center' : 'ml-4 min-w-0'}>
      <h1 className={`${styles.title} font-bold truncate ${isLight ? 'text-white' : 'text-gray-900'} ${isStacked ? 'mb-4' : ''}`}>
        {APP_NAME}
      </h1>
      {showSubtitle && (
        <p className={`${styles.subtitle} truncate ${isLight ? 'text-white opacity-90' : 'text-gray-500'} ${isStacked ? 'mb-8' : ''}`}>
          {t('systemSubtitle')}
        </p>
      )}
    </div>
  );

  if (isStacked) {
    return (
      <div className={className}>
        {logoBox}
        {textBlock}
      </div>
    );
  }

  return (
    <div className={`flex items-center ${className}`}>
      {logoBox}
      {textBlock}
    </div>
  );
};
